import { Button, Col, Create, DatePicker, Divider, Form, getValueFromEvent, Icons, Input, Row, SaveButton, Select, Upload, useFileUploadState, useForm, useSelect } from "@pankod/refine-antd";
import { IResourceComponentsProps, useApiUrl, useGetIdentity, useOne } from "@pankod/refine-core";
import { InputNumber, message, Typography } from "antd";
import { ShowUserCard } from "components/profile/showUserCard";
import { CancelButton } from "components/utils/cancelButton";
import dayjs from "dayjs";
import { ICloudOrganization, ICloudOrganizationGroup, ICloudScreening, ICountry, ILookup, IScreeningMetadata, IUser } from "interfaces";
import { MediaType } from "interfaces/enum";
import { useRef, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_LOOKUP, DATAPROVIDER_UPLOAD, getGUID, handleSelectChange, RESOURCE_PATH, STALE_DURATION } from "scripts/site";

export const CloudScreeningCreate: React.FC<IResourceComponentsProps> = () => {

  // State to manage physical location and cloud organization selection
  const [physicalLocation, setPhysicalLocation] = useState<string>("");
  const [cloudOrganizationId, setCloudOrganizationId] = useState<string | undefined>("");

  const { data: userData } = useGetIdentity(); // Fetch current user data who is logged in
  const userObj = userData as IUser;

  // Initialize form with refine's useForm hook for managing CloudScreeningCreate Component creation
  const { formProps, saveButtonProps, form } = useForm<ICloudScreening>({
    dataProviderName: DATAPROVIDER_CREATE,
    redirect: "list",
  });

  // Fetch CloudScreeningCreate Component metadata configuration which includes field definitions and validation rules
  const { data: cloudScreeningMetadata } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.CLOUD_SCREENING,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION,
    },
  });

  // Extract configuration and fields from metadata
  const supplierIds = cloudScreeningMetadata?.data.config?.supplierId || [];

  const { onChange: onUploadChange, isLoading: isUploading } = useFileUploadState();

  // useRef to ensure GUID generated once per component mount
  const guidRef = useRef<string>(getGUID());

  const initialValues = {
    id: guidRef.current,
    managerUserId: userObj?.id,
    screeningType: "HireRight",
    name: "",
    comments: "Cloud Screening request submitted.",
  };

  // Select hook for user lookup with filtering for active users
  const { selectProps: associateProps } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.PEOPLE,
    optionLabel: "nameEmail",
    optionValue: "id",
    filters: [
      {
        field: "stateManager.state",
        operator: "eq",
        value: "active",
      },
      {
        field: "id",
        operator: "ne",
        value: userObj?.id, // Exclude current user
      },
      {
        field: "id",
        operator: "contains",
        value: "@microsoft.com",
      },
    ],
    sort: [
      {
        field: "lastAccessedAt",
        order: "desc",
      },
    ],
  });

  // Select hook for country lookup
  const { selectProps: countryProps, queryResult: countryQueryResult } = useSelect<ICountry>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.COUNTRY,
    optionLabel: "name",
    optionValue: "id",
    sort: [
      {
        field: "name",
        order: "asc",
      },
    ],
    fetchSize: 100,
    filters: [
      {
        field: "cloudScreening.enabled",
        operator: "eq",
        value: true,
      },
    ],
  });

  // Select hook for cloud organization and group lookup
  const { selectProps: cloudOrganizationProps } = useSelect<ICloudOrganization>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.CLOUD_ORGANIZATION,
    optionLabel: "name",
    optionValue: "id",
    fetchSize: 100,
    filters: [
      {
        field: "enabled",
        operator: "eq",
        value: true,
      },
    ],
    sort: [
      {
        field: "name",
        order: "asc"
      },
    ],
  });

  // This will fetch cloud groups based on the selected organization
  const { selectProps: cloudOrganizationGroupProps, queryResult: cloudOrganizationGroupQueryResult } = useSelect<ICloudOrganizationGroup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.CLOUD_ORGANIZATION_GROUP,
    optionLabel: "name",
    optionValue: "id",
    fetchSize: 100,
    sort: [
      {
        field: "name",
        order: "asc"
      },
    ],
    filters: [
      {
        field: "cloudOrganizationId",
        operator: "eq",
        value: cloudOrganizationId,
      },
      {
        field: "enabled",
        operator: "eq",
        value: true,
      },
    ],
    queryOptions: {
      enabled: (cloudOrganizationId !== undefined && cloudOrganizationId !== ""), // Only fetch if cloudOrganization is selected
    },
  });

  const apiUrl = useApiUrl(DATAPROVIDER_UPLOAD); // Get API URL for file upload
  const todaysDate = dayjs().add(1, "day"); // Add one day to today's date to prevent past dates

  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 16 },
  }; // Responsive layout for two columns

  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 }; // Gutter for row spacing

  return (
    <Create
      title="Cloud Screening Request"
      saveButtonProps={saveButtonProps}
      footerButtons={() => (
        <>
          <SaveButton {...saveButtonProps} loading={isUploading}>Submit</SaveButton>
          <CancelButton />
        </>
      )}
    >
      <Typography.Text>"Cloud Screening" refers to obtaining attestation from HR. There is a standard format that HR must use, which involves Adobe Sign. The TCS HR team will complete the attestation on the MS portal using Adobe Sign. Once the necessary information is provided to HR, they will issue a letter within 5 business days.</Typography.Text>
      <Divider></Divider>

      <Form
        {...formProps}
        autoComplete="off"
        layout="vertical"
        initialValues={initialValues}
      >
        {/* Hidden id field to save GUID */}
        <Form.Item name="id" hidden={true}>
          <Input />
        </Form.Item>

        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="TCS Employee Id"
              name="tcsEmployeeId"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  type: "number",
                },
              ]}
            >
              <InputNumber placeholder="Enter TCS Employee Id" maxLength={7} controls={false} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Associate"
              name="associateId"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please select an Associate",
                },
              ]}
            >
              <Select
                placeholder="Search Name or Email"
                allowClear
                showSearch
                {...associateProps}
                onChange={(e, option) => {
                  form.setFieldsValue({ name: (option && !Array.isArray(option) ? option.label : undefined) });
                  handleSelectChange(form, e, "associateId");
                }}

              />
            </Form.Item>
          </Col>

          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              hidden
              label="Name"
              name="name"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>TCS Manager</Typography.Title>
            <Typography.Text>
              <ShowUserCard id={userObj?.id} />
            </Typography.Text>
            <Form.Item
              hidden
              label="TCS Manager"
              name={["managerUserId"]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Supplier Id"
              name="supplierId"
              tooltip="Please check the PO where current Microsoft alias is tagged in Employee Central (aka Headtrax)"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                placeholder="Select Supplier Id"
                allowClear
                options={supplierIds?.map((supplier) => ({
                  label: supplier,
                  value: supplier,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Cloud Organization and Group Selection */}
        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Cloud Organization"
              name="cloudOrganizationId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                {...cloudOrganizationProps}
                allowClear
                placeholder="Select Cloud Organization"
                showSearch
                onChange={function (val, option) {
                  setCloudOrganizationId(val?.toString());
                  form?.setFieldsValue({
                    cloudOrganizationGroupId: "", // Clear cloudOrganizationGroupId
                    // cloudOrganizationName: (option && !Array.isArray(option) ? option?.label : undefined)
                  });
                }}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Cloud Group"
              name="cloudOrganizationGroupId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                {...cloudOrganizationGroupProps}
                allowClear
                placeholder="Select Cloud Organization Group"
                showSearch
                disabled={(!cloudOrganizationId) || cloudOrganizationGroupQueryResult?.isFetching}
                loading={cloudOrganizationGroupProps?.loading || cloudOrganizationGroupQueryResult?.isFetching} // loading state
              // onChange={function (val, option) {
              //   form.setFieldsValue({ cloudOrganizationGroupName: (option && !Array.isArray(option) ? option?.label : undefined) });
              // }}
              />
            </Form.Item>
          </Col>

        </Row>
        <Divider orientation="left">Location</Divider>

        {/* Location Selection - Filtered by selected country */}

        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Physical Location"
              name="physicalLocationId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                placeholder="Select Physical Location"
                {...countryProps}
                showSearch
                allowClear
                onChange={function (val) {
                  setPhysicalLocation(val?.toString());
                  form.setFieldsValue({
                    employeeCentralLocationId: null,
                    screeningId: null,
                    screeningDate: null
                  }); // Clear employeeCentralLocationId
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Employee Central Location (aka Headtrax)"
              name="employeeCentralLocationId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (value === physicalLocation) {
                      return Promise.resolve();
                    }
                    else {
                      return Promise.reject("Employee Central Location must match Physical Location");
                    }
                  },
                },
              ]}
            >
              <Select
                allowClear
                placeholder="Select Employee Central Location"
                showSearch
                {...countryProps}
                disabled={form.getFieldValue("physicalLocationId") === ""}
              />
            </Form.Item>
          </Col>
        </Row>

        {physicalLocation
          && countryQueryResult.data?.data?.find((d) => d.id === physicalLocation)?.cloudScreening?.isScreeningDataRequired === true
          && (
            <>
              <Row gutter={rowGutter}>
                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    label="Screening Id"
                    name="screeningId"
                    tooltip={"HireRight screening Id"}

                    rules={[
                      {
                        required: true,
                        type: "string",
                      },
                      {
                        validator: (_, value) => {
                          const hireRightRegex = /^\w{2}-\d{6}-[A-Z0-9]{5}$/; // Regex for HireRight ID format without enforcing starting characters
                          if (!value || hireRightRegex.test(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject("Invalid HireRight Screening Id. Format: <2-character code>-<6-digit number>-<5-character alphanumeric code>");
                        },
                      },
                    ]}
                  >
                    <Input placeholder={"AA-000000-AAAAA"} />
                  </Form.Item>
                </Col>

                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    label="Screening Date"
                    name="screeningDate"
                    tooltip="HireRight Screening completion date"
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        type: "date"
                      },
                    ]}
                  >
                    <DatePicker
                      picker="date"
                      style={{ width: 280 }}
                      disabledDate={(selDate) => selDate && (selDate > todaysDate || selDate < dayjs().subtract(22, 'months'))}
                    />
                  </Form.Item>
                </Col>

                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    hidden
                    label="Screening Type"
                    name="screeningType"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        <Form.Item name="comments" hidden={true}>
          <Input />
        </Form.Item>
        <Row gutter={rowGutter}>
          <Col className="gutter-row">
            <Form.Item
              label="Screenshot of Employee Central Location which highlights the Physical Location"
              name="screenshot"
              valuePropName="fileList"
              getValueFromEvent={getValueFromEvent}
              rules={[
                {
                  required: true,
                  message: "Please upload screenshot of Employee Central Location!",
                },
              ]}
            >
              <Upload
                name="file"
                accept="image/*"
                //action={`${apiUrl}/${RESOURCE_PATH.CLOUD_SCREENING}/${MediaType.Document}`}
                action={`${apiUrl}/${RESOURCE_PATH.CLOUD_SCREENING}/${initialValues?.id ?? MediaType.Document}`}
                listType="text"
                onChange={onUploadChange}
                maxCount={1}
                beforeUpload={(file: File) => {
                  const isImageFile = file.type.search("image") === 0;
                  if (!isImageFile) {
                    message.error(`${file.name} is not an image file!`);
                  }
                  const isLt2M = file.size / 1024 / 1024 < 5;
                  if (!isLt2M) {
                    message.error(`${file.name} is greater than 5MB!`);
                  }
                  return (isImageFile && isLt2M) ? true : Upload.LIST_IGNORE; // Prevent upload if not image or too large
                }}
              >
                <Button icon={<Icons.UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Create >
  );
};
